/**
 * Lógica pura de checagem de permissions (sem React/store), para ser testável
 * em ambiente node e reusada pelo hook usePermissions.
 *
 * Espelha a semântica do backend (app/api/deps.py):
 * - permissions no formato "resource:action" (ex.: "vms:create")
 * - super admin (is_superuser) faz bypass de qualquer checagem
 * - o escopo é a organização ativa (membership), não as permissions globais
 */

export type PermissionChecker = {
  /** Permissions efetivas da org ativa (ou globais, como fallback). */
  permissions: string[]
  isSuperuser: boolean
  /** True se tem a permission exata (ou é super admin). */
  has: (perm: string) => boolean
  /** True se tem PELO MENOS UMA das permissions (OR). */
  hasAny: (perms: string[]) => boolean
  /** True se tem TODAS as permissions (AND). */
  hasAll: (perms: string[]) => boolean
}

export function makePermissionChecker(
  permissions: string[] | undefined | null,
  isSuperuser: boolean,
): PermissionChecker {
  const list = permissions ?? []
  const set = new Set(list)
  return {
    permissions: list,
    isSuperuser,
    has: (perm) => isSuperuser || set.has(perm),
    hasAny: (perms) => isSuperuser || perms.some((p) => set.has(p)),
    hasAll: (perms) => isSuperuser || perms.every((p) => set.has(p)),
  }
}

type MembershipLike = {
  organization_id: string
  permissions: string[]
}

/**
 * Resolve a lista de permissions da organização ativa. Faz fallback para as
 * permissions globais do usuário quando a membership ativa não está disponível
 * (ex.: super admin sem membership na org corrente).
 */
export function resolveActivePermissions(
  memberships: MembershipLike[] | undefined | null,
  activeOrganizationId: string | null | undefined,
  userPermissions: string[] | undefined | null,
): string[] {
  const active = (memberships ?? []).find((m) => m.organization_id === activeOrganizationId)
  return active?.permissions ?? userPermissions ?? []
}
